"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const wp_query_params = {
  per_page: 3,
  order: "desc",
  orderby: "date"
};
const wp_query = new WP_Query(wp_query_params);
function GalleryConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-6">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className={`aspect-[3/4] bg-card backdrop-blur-md border border-border/20 ${i === 1 ? "md:mt-20" : ""}`} style={{
        boxShadow: "var(--shadow-3xl)"
      }} />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-20">
        <p className="font-mono text-sm uppercase tracking-widest text-muted-foreground mb-4">
          Signal Lost: Failed to load transmissions
        </p>
        <Button variant="outline" onClick={refetch} className="font-mono uppercase tracking-widest rounded-md border-border text-foreground hover:bg-accent hover:text-accent-foreground">
          Reconnect
        </Button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-center text-muted-foreground font-sans italic text-lg">
        The vault is currently sealed. No events found.
      </p>;
  }
  return <div className="grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-6">
      {products.map((product, i) => <article key={product.id} data-index={i} className={`group relative bg-card backdrop-blur-md border border-border/20 transition-all duration-500 hover:border-[var(--chart-1)] ${i === 1 ? "md:mt-20" : ""}`} style={{
      boxShadow: "var(--shadow-3xl)"
    }}>
          {}
          <div className="relative aspect-[3/4] overflow-hidden bg-secondary">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04] grayscale-[20%] group-hover:grayscale-0" /> : <div className="h-full w-full bg-secondary" />}

            {}
            <div className="absolute top-5 right-5 flex items-center gap-2">
              <span className="inline-block w-2 h-2 bg-[var(--chart-2)] animate-pulse" style={{
            boxShadow: "0 0 12px var(--chart-2)"
          }} aria-hidden="true" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-foreground/70">
                Live Stream
              </span>
            </div>

            {}
            <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-out">
              <AddToCartButton productId={product.id} variant="default" size="lg" text="Purchase Access" showQuantity={false} className="w-full" buttonClassName="bg-primary text-primary-foreground w-full rounded-md font-mono uppercase tracking-[0.2em] text-xs font-semibold h-14" />
            </div>
          </div>

          {}
          <div className="p-6 md:p-7 space-y-3">
            <Link to={product.link} className="block group/title">
              <h3 className="font-default font-bold uppercase tracking-tight text-xl md:text-2xl text-foreground leading-tight">
                {product.title}
              </h3>
            </Link>

            {product.short_description ? <div className="font-sans text-sm text-muted-foreground leading-relaxed [&_p]:mb-0 [&_p:last-child]:mb-0 line-clamp-2" dangerouslySetInnerHTML={{
          __html: product.short_description
        }} /> : <p className="font-sans text-sm text-muted-foreground">
                Immersive VR-ready event capture from the underground.
              </p>}

            <div className="pt-3 flex items-baseline gap-2">
              {product.discounted_price !== undefined && product.discounted_price !== null ? <>
                  <span className="font-mono font-bold text-2xl text-foreground tabular-nums">
                    ${product.discounted_price}
                  </span>
                  <span className="font-mono text-sm text-muted-foreground line-through tabular-nums">
                    ${product.price}
                  </span>
                </> : <span className="font-mono font-bold text-2xl text-foreground tabular-nums">
                  ${product.price}
                </span>}
            </div>
          </div>
        </article>)}
    </div>;
}
export default function RecentIndulgences() {
  return <section data-section-id="3893" id="recent-indulgences" className="relative bg-background text-foreground py-32 md:py-60 px-6 md:px-12 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-20 md:mb-28">
          {}
          <div className="lg:col-span-7">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--chart-1)] mb-8">
              01 — Recent Indulgences
            </p>
            <h2 className="font-default font-bold uppercase tracking-tighter leading-[0.9] text-foreground text-6xl sm:text-7xl md:text-8xl lg:text-[8.75rem]">
              <span className="block">
                The{" "}
                <span className="text-[var(--chart-1)] font-serif italic font-light" aria-hidden="true">
                  /
                </span>
              </span>
              <span className="block">Vault</span>
            </h2>
          </div>

          {}
          <div className="lg:col-span-5 flex flex-col justify-start gap-8 lg:pt-4">
            <p className="font-sans text-lg md:text-xl text-muted-foreground leading-relaxed max-w-md">
              A curated archive of high-fidelity music concerts, nightclub rituals, and immersive art shows. Experience the pulse of the night through VR eyewear.
            </p>

            <Link to="/VaultPage" className="group inline-flex items-center gap-3 font-mono uppercase tracking-[0.2em] text-xs font-semibold text-foreground border-b border-border/20 hover:border-[var(--chart-1)] pb-3 w-fit transition-colors duration-300">
              <span>Enter The Archive</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-2 text-[var(--chart-1)]" strokeWidth={1.5} />
            </Link>
          </div>
        </div>

        {}
        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        order: "desc",
        orderby: "date"
      }}>
          <GalleryConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}